"use client";

import { useState } from "react";
import { promos, formatPrice } from "@/lib/data";
import { useCart, useUI } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Clock, ShoppingCart, Crown, Zap, Armchair, CookingPot, HomeIcon, Sparkles } from "lucide-react";
import Image from "next/image";

const experienceSteps = [
  { n: "1", title: "Explorez", sub: "Les 5 Mondes", desc: "Naviguez dans nos univers technologiques exceptionnels" },
  { n: "2", title: "Contemplez", sub: "Les 7 Merveilles", desc: "Découvrez des joyaux de prestige et de beauté" },
  { n: "3", title: "Choisissez", sub: "Merveilles Présidentielles & Platinum", desc: "Sélectionnez les produits d'exception qui vous ressemblent" },
  { n: "4", title: "Vivez", sub: "L'Expérience", desc: "Profitez du service concierge et de l'excellence" },
];

const showcases = [
  { icon: Armchair, tag: "Gaming Experience", title: "Le Setup de vos Rêves", desc: "RTX 5090, OLED 240Hz, clavier méca RGB. La puissance au service de votre passion." },
  { icon: CookingPot, tag: "Robot Cuisine", title: "La Cuisine Réinventée", desc: "Robot Samsung Food, 100+ recettes sénégalaises et connectivité SmartThings." },
  { icon: Zap, tag: "Wind-Free AI", title: "Votre Maison Protégée", desc: "Climatisation silencieuse pilotée par IA, sécurité 4K et sérénité absolue." },
  { icon: HomeIcon, tag: "Bespoke Design", title: "Maison d'Exception", desc: "Bespoke, Wind-Free, SmartThings. Votre villa mérite l'excellence." },
];

export function Experience() {
  const openBoutique = useUI((s) => s.openBoutique);

  // Chaque showcase « Découvrir » mène à la boutique du monde correspondant
  const showcaseMonde: Record<string, string> = {
    "Le Setup de vos Rêves": "cloud",
    "La Cuisine Réinventée": "electro",
    "Votre Maison Protégée": "electro",
    "Maison d'Exception": "iot",
  };

  return (
    <section className="py-16 sm:py-20 bg-[#04140D] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 45% 40% at 15% 20%, rgba(0,107,63,0.4), transparent), radial-gradient(ellipse 40% 35% at 85% 80%, rgba(255,215,0,0.1), transparent)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs font-bold tracking-[0.3em] text-[#FFD700] uppercase mb-3">
            Le Voyage Technologique
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
            L&apos;<span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">Expérience</span>
          </h2>
          <div className="senegal-bar mx-auto mt-5" />
          <p className="mt-4 text-white/60 leading-relaxed">
            Votre Voyage Commence Ici — Un parcours d&apos;exception au cœur de la technologie de
            luxe. Chaque étape vous rapproche de l&apos;excellence, chaque produit est une œuvre
            d&apos;art que l&apos;on désire posséder.
          </p>
        </div>

        {/* Étapes */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {experienceSteps.map((step, idx) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="relative p-6 rounded-3xl bg-white/[0.04] border border-white/10 hover:border-[#FFD700]/40 transition-colors"
            >
              <span className="text-5xl font-black bg-gradient-to-br from-[#FFD700]/40 to-[#FFD700]/10 bg-clip-text text-transparent">
                {step.n}
              </span>
              <h3 className="text-xl font-black text-white mt-3">{step.title}</h3>
              <p className="text-[#FFD700] text-sm font-semibold">{step.sub}</p>
              <p className="text-white/50 text-sm mt-2 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Showcases */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {showcases.map((s) => (
            <div
              key={s.title}
              className="group p-6 rounded-3xl bg-gradient-to-b from-white/[0.05] to-transparent border border-white/10 hover:border-[#FFD700]/40 transition-all hover:-translate-y-1"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#006B3F] to-[#008751] border border-[#FFD700]/25 flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-[#FFD700]" />
              </div>
              <p className="text-[10px] font-bold tracking-[0.2em] text-[#00B86B] uppercase">{s.tag}</p>
              <h3 className="text-lg font-black text-white mt-1">{s.title}</h3>
              <p className="text-white/50 text-sm mt-2 leading-relaxed">{s.desc}</p>
              <button
                type="button"
                onClick={() => openBoutique(showcaseMonde[s.title] ?? "tout")}
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#FFD700] group-hover:gap-3 transition-all cursor-pointer"
              >
                Découvrir →
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Promos() {
  const addItem = useCart((s) => s.addItem);
  const [toutAfficher, setToutAfficher] = useState(false);

  // VIII) « Profiter » : ajout du pack promo au panier à partir du prix d'appel
  const profiter = (promo: (typeof promos)[number]) => {
    addItem({
      id: promo.id,
      name: `Pack ${promo.title}`,
      price: promo.fromPrice,
      image: promo.image,
      presidential: promo.presidential,
    });
  };

  const promosVisibles = toutAfficher ? promos : promos.slice(0, 6);

  return (
    <section id="promos" className="py-16 sm:py-20 bg-[#F5FBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-[0.3em] text-[#CE1126] uppercase mb-3">
            Offres Limitées & Exclusives
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
            Promos du{" "}
            <span className="bg-gradient-to-r from-[#CE1126] to-[#FF6B6B] bg-clip-text text-transparent">
              Moment
            </span>
          </h2>
          <div className="senegal-bar mx-auto mt-5" />
          <p className="mt-4 text-gray-500 leading-relaxed">
            Des offres d&apos;exception à durée limitée. Ne ratez aucune opportunité avec nos
            promotions flash prestige et nos soldes exclusives.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {promosVisibles.map((promo, idx) => (
            <motion.div
              key={promo.id}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (idx % 2) * 0.1 }}
              className={`relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group ${
                promo.presidential
                  ? "bg-gradient-to-br from-[#171106] to-[#0B0804] border-2 border-[#FFD700]/40"
                  : "bg-white border border-gray-100"
              }`}
            >
              <div className="grid sm:grid-cols-[2fr_3fr]">
                <div className="relative min-h-[220px] overflow-hidden">
                  <Image
                    src={promo.image}
                    alt={promo.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-700"
                    sizes="(max-width: 768px) 100vw, 300px"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
                </div>
                <div className="p-6 sm:p-7">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest ${
                        promo.presidential
                          ? "bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black"
                          : "bg-gradient-to-r from-[#006B3F] to-[#008751] text-white"
                      }`}
                    >
                      {promo.presidential ? <Crown className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                      {promo.badge}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-semibold ${
                        promo.presidential ? "text-[#FFE87C]" : "text-[#006B3F]"
                      }`}
                    >
                      <Clock className="w-3.5 h-3.5" /> {promo.endsIn}
                    </span>
                  </div>

                  <div className="mt-3 flex items-start gap-3">
                    <span
                      className={`text-4xl font-black leading-none ${
                        promo.presidential
                          ? "bg-gradient-to-br from-[#FFD700] to-[#DAA520] bg-clip-text text-transparent"
                          : "text-[#CE1126]"
                      }`}
                    >
                      -{promo.discount}%
                    </span>
                    <div className="min-w-0">
                      <h3
                        className={`font-black text-lg leading-snug ${
                          promo.presidential ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {promo.title}
                      </h3>
                      <p
                        className={`text-xs font-semibold ${
                          promo.presidential ? "text-[#FFD700]" : "text-[#006B3F]"
                        }`}
                      >
                        {promo.subtitle}
                      </p>
                    </div>
                  </div>

                  <p
                    className={`text-xs mt-3 leading-relaxed ${
                      promo.presidential ? "text-white/55" : "text-gray-500"
                    }`}
                  >
                    {promo.description}
                  </p>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {promo.items.map((item) => (
                      <span
                        key={item}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          promo.presidential
                            ? "bg-[#FFD700]/8 text-[#FFE87C] border-[#FFD700]/25"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }`}
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                  <div
                    className={`mt-4 flex items-center justify-between gap-2 border-t pt-4 ${
                      promo.presidential ? "border-[#FFD700]/15" : "border-gray-100"
                    }`}
                  >
                    <div>
                      <p className={`text-[10px] uppercase tracking-wide ${promo.presidential ? "text-white/40" : "text-gray-400"}`}>
                        À partir de
                      </p>
                      <p className={`font-black ${promo.presidential ? "text-[#FFD700]" : "text-gray-900"}`}>
                        {formatPrice(promo.fromPrice)}{" "}
                        <span className="text-[10px] text-gray-400">FCFA</span>
                      </p>
                    </div>
                    <Button
                      onClick={() => profiter(promo)}
                      className={
                        promo.presidential
                          ? "bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black font-black hover:shadow-[0_0_25px_rgba(255,215,0,0.4)]"
                          : "bg-gradient-to-r from-[#CE1126] to-[#E84040] hover:shadow-lg text-white"
                      }
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" /> Profiter
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Button
            variant="outline"
            onClick={() => setToutAfficher((v) => !v)}
            className="border-[#006B3F]/30 text-[#006B3F] hover:bg-[#006B3F] hover:text-white"
          >
            {toutAfficher ? "Réduire les promotions" : `Voir toutes les promotions (${promos.length})`}
          </Button>
        </div>
      </div>
    </section>
  );
}
